import { Radio } from '@base-ui/react/radio';
import { RadioGroup as BaseUIRadioGroup } from '@base-ui/react/radio-group';
import React, { createContext, useContext, useId, useMemo } from 'react';

import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Body } from '../../text/Body';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { useControllableState } from '../../utilities/useControllableState';
import {
  markColor,
  raisedDisabledSelectedStyles,
  raisedDisabledStyles,
  raisedEnabledSelectedStyles,
  raisedEnabledStyles,
  selectors,
  transitions,
} from '../shared/styles';

interface RadioContextValue {
  isDisabled?: boolean;
  selectedValue: string | null;
}

const RadioContext = createContext<RadioContextValue>({ selectedValue: null });

const RadioInputMark = styled('div', {
  width: '$6',
  height: '$6',
  borderRadius: '99rem',
  backgroundColor: markColor,
  transition: transitions.control,

  variants: {
    isSelected: {
      true: {
        display: 'flex',
      },
      false: {
        display: 'none',
      },
    },
  },
});
RadioInputMark.displayName = 'RadioInputMark';

const RadioInputControl = styled(Radio.Root, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  widthAll: '$12',
  heightAll: '$12',
  borderRadius: '99rem',
  cursor: 'pointer',
  userSelect: 'none',
  transition: transitions.control,

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    isSelected: {
      true: {},
      false: {},
    },
    isDisabled: {
      true: {
        cursor: 'not-allowed',
        opacity: 0.5,
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      isSelected: false,
      isDisabled: false,
      css: raisedEnabledStyles,
    },
    {
      isSelected: true,
      isDisabled: false,
      css: raisedEnabledSelectedStyles,
    },
    {
      isSelected: false,
      isDisabled: true,
      css: raisedDisabledStyles,
    },
    {
      isSelected: true,
      isDisabled: true,
      css: raisedDisabledSelectedStyles,
    },
  ],
});
RadioInputControl.displayName = 'RadioInputControl';

const RadioInputLabel = styled(Body, {
  cursor: 'pointer',

  variants: {
    isDisabled: {
      true: {
        color: colors.bodyNeutralLight,
        cursor: 'not-allowed',

        [darkThemeSelector]: {
          color: colors.bodyNeutralDark,
        },
      },
      false: {},
    },
    isSelected: {
      true: {
        color: colors.headingBrandLight,

        [darkThemeSelector]: {
          color: colors.headingBrandDark,
        },
      },
      false: {},
    },
  },
});
RadioInputLabel.displayName = 'RadioInputLabel';

const RadioInputContainer = styled('label', {
  display: 'flex',
  cursor: 'pointer',
});
RadioInputContainer.displayName = 'RadioInputContainer';

interface RadioInputProps {
  /**
   * The label of the radio input.
   */
  children: React.ReactNode;
  /**
   * The value of the radio input.
   */
  value: string;
  /**
   * Whether the radio input is disabled.
   */
  isDisabled?: boolean;
}

export function RadioInput({ children, isDisabled, value }: RadioInputProps) {
  const context = useContext(RadioContext);
  const disabled = Boolean(isDisabled || context.isDisabled);
  const isSelected = context.selectedValue === value;

  return (
    <RadioInputContainer>
      <AlignStack
        direction="row"
        gap={6}
        preset="body"
        start={
          <RadioInputControl
            disabled={disabled}
            isDisabled={disabled}
            isSelected={isSelected}
            value={value}
          >
            <RadioInputMark isSelected={isSelected} />
          </RadioInputControl>
        }
      >
        <RadioInputLabel isDisabled={disabled} isSelected={isSelected}>
          {children}
        </RadioInputLabel>
      </AlignStack>
    </RadioInputContainer>
  );
}

const RadioGroupLabel = styled(Body, {
  display: 'flex',
});
RadioGroupLabel.displayName = 'RadioGroupLabel';

const RadioGroupContainer = styled(BaseUIRadioGroup, {
  display: 'flex',

  variants: {
    direction: {
      column: {
        flexDirection: 'column',
        gap: '$8',
      },
      row: {
        flexDirection: 'row',
        gap: '$12',
      },
    },
    isDisabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
      },
      false: {},
    },
  },
});
RadioGroupContainer.displayName = 'RadioGroupContainer';

interface RadioGroupProps extends React.AriaAttributes {
  id?: string;
  /**
   * The radio fields of the radio group.
   */
  children: React.ReactNode;
  /**
   * The orientation of the radio group.
   */
  direction?: 'column' | 'row';
  /**
   * The default value of the radio group.
   */
  defaultValue?: string;
  /**
   * The value of the radio group.
   */
  value?: string;
  /**
   * The callback function that is called when the radio group value changes.
   */
  onChange?: (value: string) => void;
  /**
   * Whether the radio group is disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether the radio group is read-only.
   */
  isReadOnly?: boolean;
  /**
   * Whether the radio group is required.
   */
  isRequired?: boolean;
  /**
   * The label of the radio group.
   */
  label?: React.ReactNode;
  /**
   * The name of the radio group.
   */
  name: string;
}

export function RadioGroup({
  children,
  defaultValue,
  direction = 'column',
  isDisabled,
  isReadOnly,
  isRequired,
  label,
  name,
  onChange,
  value,
  ...ariaProps
}: RadioGroupProps) {
  const labelId = useId();
  const [selectedValue, setSelectedValue] = useControllableState<string | null>({
    defaultValue: defaultValue ?? null,
    onChange: (next) => {
      if (next !== null) onChange?.(next);
    },
    value,
  });

  const contextValue = useMemo(() => ({ isDisabled, selectedValue }), [isDisabled, selectedValue]);

  return (
    <RadioGroupContainer
      {...ariaProps}
      aria-labelledby={ariaProps['aria-labelledby'] ?? (label ? labelId : undefined)}
      aria-orientation={direction === 'row' ? 'horizontal' : 'vertical'}
      direction={direction}
      disabled={isDisabled}
      isDisabled={isDisabled}
      name={name}
      onValueChange={(next) => {
        if (typeof next === 'string') setSelectedValue(next);
      }}
      readOnly={isReadOnly}
      required={isRequired}
      value={selectedValue}
    >
      {label && <RadioGroupLabel id={labelId}>{label}</RadioGroupLabel>}
      <RadioContext.Provider value={contextValue}>{children}</RadioContext.Provider>
    </RadioGroupContainer>
  );
}
